import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Text from '../../typography/Text';
import List from './components/List';
import Link from './components/Link';
import TableWrapper from './components/TableWrapper';
import { sanitizeMarkdownUrl } from './sanitizeUrl';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Markdown');

const Markdown = memo(
    ({
        classes: classesProp,
        className: classNameProp,
        components,
        content,
        linkCallback,
        overrides: overridesProp,
        showTableActions = false,
    }) => {
        const classes = useClasses(useStyles, classesProp);
        const override = getOverrides(overridesProp, Markdown.overrides);

        const rootProps = useMemo(
            () => ({
                children: content,
                remarkPlugins: [remarkGfm],
                components: {
                    a: ({ node, children, href, ...props }) => (
                        <Link
                            variation="primary"
                            callback={linkCallback}
                            {...props}
                            {...override.a}
                            href={sanitizeMarkdownUrl(href)}
                        >
                            {children}
                        </Link>
                    ),
                    p: ({ children }) => (
                        <Text type="body" as="p" className={classes.block} {...override.p}>
                            {children}
                        </Text>
                    ),
                    h1: ({ children }) => (
                        <Text type="h5" as="h1" className={classes.block} {...override.h1}>
                            {children}
                        </Text>
                    ),
                    h2: ({ children }) => (
                        <Text type="h6" as="h2" className={classes.block} {...override.h2}>
                            {children}
                        </Text>
                    ),
                    h3: ({ children }) => (
                        <Text
                            type="subtitleLg"
                            medium
                            as="h3"
                            className={classes.block}
                            {...override.h3}
                        >
                            {children}
                        </Text>
                    ),
                    h4: ({ children }) => (
                        <Text type="subtitle1" as="h4" className={classes.block} {...override.h4}>
                            {children}
                        </Text>
                    ),
                    h5: ({ children }) => (
                        <Text
                            type="subtitle"
                            medium
                            as="h5"
                            className={classes.block}
                            {...override.h5}
                        >
                            {children}
                        </Text>
                    ),
                    h6: ({ children }) => (
                        <Text type="body" medium as="h6" className={classes.block} {...override.h6}>
                            {children}
                        </Text>
                    ),
                    ul: ({ children }) => <List items={children} {...override.ul} />,
                    ol: ({ children }) => <List items={children} type="ordered" {...override.ol} />,
                    li: ({ children, ...props }) => (
                        <Text type="body" as="li" {...props} {...override.li}>
                            {children}
                        </Text>
                    ),
                    table: ({ children, ...props }) => {
                        const tableEl = (
                            <table className={classes.table} {...props}>
                                {children}
                            </table>
                        );
                        return showTableActions ? <TableWrapper>{tableEl}</TableWrapper> : tableEl;
                    },
                    thead: ({ children, ...props }) => <thead {...props}>{children}</thead>,
                    tbody: ({ children, ...props }) => <tbody {...props}>{children}</tbody>,
                    tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
                    th: ({ children, ...props }) => (
                        <th className={classes.tableHeaderCell} {...props}>
                            {children}
                        </th>
                    ),
                    td: ({ children, ...props }) => (
                        <td className={classes.tableRowCell} {...props}>
                            {children}
                        </td>
                    ),
                    ...components,
                    img: ({ src, alt }) => {
                        const safeSrc = sanitizeMarkdownUrl(src);
                        if (!safeSrc || !/^https?:\/\//i.test(safeSrc)) {
                            return alt ? <span>{alt}</span> : null;
                        }
                        return <img src={safeSrc} alt={alt || ''} />;
                    },
                },
                ...override.root,
                skipHtml: true,
                urlTransform: sanitizeMarkdownUrl,
            }),
            [
                classes.block,
                classes.table,
                classes.tableHeaderCell,
                classes.tableRowCell,
                components,
                content,
                linkCallback,
                override.a,
                override.h1,
                override.h2,
                override.h3,
                override.h4,
                override.h5,
                override.h6,
                override.ol,
                override.p,
                override.root,
                override.ul,
                override.li,
                showTableActions,
            ],
        );

        return (
            <div className={classNames(classes.root, classNameProp)}>
                <ReactMarkdown {...rootProps} />
            </div>
        );
    },
);

Markdown.overrides = [
    'root',
    'a',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
];

Markdown.displayName = 'Markdown';

Markdown.propTypes = {
    /** Custom CSS class for styling the Markdown container */
    className: PropTypes.string,
    /**
     * Custom components to override default markdown rendering.
     * Can be used to provide custom renderers for specific markdown elements.
     * Each key should be a valid markdown element tag (e.g., 'a', 'p', 'h1').
     */
    components: PropTypes.object,
    /**
     * Markdown content to render.
     * Raw markdown string that will be processed and converted to React components.
     */
    content: PropTypes.string.isRequired,
    /**
     * Callback function that is called when a link in the markdown content is clicked.
     * Receives an object with { event, attributes, link } where:
     * - event: The click event
     * - attributes: Parsed attributes from the link title (as JSON)
     * - link: Function to execute the default link behavior
     */
    linkCallback: PropTypes.func,
    /**
     * Object with custom style overrides for inner elements.
     * Supports overrides for 'root', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
     * 'table', 'thead', 'tbody', 'tr', 'th', 'td'.
     */
    overrides: PropTypes.object,
    /**
     * Whether to show table download toolbar (CSV download icon in a BoxInfo-style wrapper).
     * When true, each table is wrapped with a header bar and download action.
     * When false, tables render without the toolbar.
     */
    showTableActions: PropTypes.bool,
};

export default Markdown;
